import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { matchPath, useLocation } from "react-router-dom";

import Button from "@material-ui/core/Button";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import Refresh from "@material-ui/icons/Refresh";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import routes from "common/routes";
import { useClickBlockerContext } from "components/BlockableClickContext";
import CustomSelect from "components/CustomSelect";
import { useExamLockerContext } from "components/ExamLockerContext/ExamLockerContext";
import FormBlock from "components/FormBlock";
import HideMenu from "components/Icons/HideMenu";
import Loader from "components/Loader";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import OverlayPage from "components/OverlayPage";
import { useSnackbar } from "components/SnackbarContext/SnackbarContext";
import {
  IWithScrollToErrorProps,
  WithScrollToError
} from "hocs/WithScrollToError";
import { useRacehorse360Api } from "hooks/api";
import { useAppSelector } from "hooks/redux/useAppSelector";
import { SortOrder } from "interfaces/SortOrder";
import { EModalConfirmationTypes } from "interfaces/VetExamState";
import { HORSE_EXAM_OPTIONS } from "pages/HorseDetailsPage/Vet/options";
import {
  setIsUseLastExamEntry,
  setModalState,
  setModalType
} from "store/actions/vetExam";
import { getTotalResultsFromData } from "utils/api-utils";
import { currentTimeZoneString, transformViewDate } from "utils/date-utils";
import CloneLastExamEntry from "./CloneLastExamEntry";
import ExamDetailsList from "./ExamDetailsList/ExamDetailsList";
import ExamFormHeader from "./ExamFormHeader";
import ExamResults from "./ExamResults";
import HorseInfoContainer from "./HorseInfoContainer";
import JogInformation from "./JogInformation";
import SubmitExamModal from "./SubmitExamModal";
import VetExamModal from "./VetExamModal";
import { useVetExamContext } from "./VetExamProvider/VetExamProvider";
import {
  checkSelectingExamFormFields,
  getExamResult,
  getReasonIdsWithConclusionField,
  getReasonNote,
  getResetLocalStateExam,
  getVetExamRequestState,
  isEventBasedExam,
  isExamCompleted,
  mapOptions
} from "./helper";
import { EVetExamFields } from "./options";

import useStyles from "./styles";

interface IProps {
  refetchData: () => void;
  onClose: () => void;
}

const ERROR_ANCHOR = "fieldErrorMessage";
const DEFAULT_EXAM_FORM_HEADER_HEIGHT = 62;

const VetExams: React.FC<IProps & IWithScrollToErrorProps> = props => {
  const { onClose, refetchData, scrollToFirstError } = props;

  const [examFormHeaderHeight, setExamFormHeaderHeight] = useState(
    DEFAULT_EXAM_FORM_HEADER_HEIGHT
  );
  const classes = useStyles({ examFormHeaderHeight });
  const { currentUser } = useLoggedInUser();
  const dispatchToRedux = useDispatch();

  const location = useLocation();
  const isRaceDayTabPath = matchPath(location.pathname, {
    path: routes.preRaceScreeningRaceDay.path
  });

  const { lockExam, unlockExam } = useExamLockerContext();
  const { setBlockClick, setBlockClickCallback } = useClickBlockerContext();
  const { modalState, modalType } = useAppSelector(state => ({
    ...state.vetExam
  }));
  const { showErrorSnack } = useSnackbar();
  const {
    formFieldsItems,
    values,
    setFieldValue,
    setValues,
    validateForm,
    setShouldValidateOnChange,
    currentExam
  } = useVetExamContext();

  const reasonId = values?.[EVetExamFields.REASON];
  const result = values?.[EVetExamFields.RESULT];

  const isCurrentExamCompleted = isExamCompleted(currentExam?.status);

  const reasonIdsWithConclusionField = getReasonIdsWithConclusionField(
    formFieldsItems?.reasons
  );

  const shouldShowConclusionField =
    reasonIdsWithConclusionField?.includes(reasonId);

  const shouldShowDateCompletionField =
    (!isRaceDayTabPath || !currentExam?.isRaceDayExam) &&
    !isCurrentExamCompleted;
  const reasonNote = getReasonNote(currentExam);

  const [isShowHorseInfoDesktop, setIsShowHorseInfoDesktop] = useState(false);
  const [isShowHorseInfoMobile, setIsShowHorseInfoMobile] = useState(false);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);

  const {
    useListWorkoutRequests,
    usePullRelevantHorseWarningMessage,
    useCancelHorseExam,
    useCompleteHorseExam,
    useEditHorseExam,
    useListHorseExams
  } = useRacehorse360Api();

  //WorkoutsExams
  const pastExamsQuery: racehorse360.IHorseExamFilter = {
    horseIds: [currentExam?.horse?.id],
    statuses: [racehorse360.HorseExamStatus.HORSE_EXAM_STATUS_COMPLETED],
    horseExamTypes: [racehorse360.HorseExamType.HORSE_EXAM_TYPE_FULL_EXAM]
  };
  const { data: pastExamsCountData } = useListHorseExams(
    {
      query: pastExamsQuery,
      pagingOptions: {
        maxResults: 1,
        includeSummary: true
      }
    },
    {
      enabled: Boolean(currentExam?.horse?.id)
    }
  );
  const pastExamsCount = getTotalResultsFromData(pastExamsCountData);

  const { data: pastExamsData, isLoading: isPastExamsLoading } =
    useListHorseExams(
      {
        query: pastExamsQuery,
        getOptions: {
          select: HORSE_EXAM_OPTIONS,
          orderBy: [`completedDate ${SortOrder.DESC}`]
        },
        pagingOptions: {
          maxResults: pastExamsCount
        }
      },
      {
        enabled: pastExamsCount > 0
      }
    );
  const completedExams = pastExamsData?.horseExams || [];

  //WorkoutRequest requests
  const {
    data: upcomingWorkoutRequests,
    isLoading: isUpcomingWorkoutRequestsLoading
  } = useListWorkoutRequests(
    {
      query: {
        horseIds: currentExam?.horse.id ? [currentExam?.horse.id] : [],
        date: {
          range: {
            relative: {
              // Today and later
              startDate: { value: 0 },
              timezone: currentTimeZoneString()
            }
          }
        },
        statuses: [
          racehorse360.WorkoutRequestStatus.WORKOUT_REQUEST_STATUS_REQUESTED,
          racehorse360.WorkoutRequestStatus.WORKOUT_REQUEST_STATUS_EXAM_PENDING,
          racehorse360.WorkoutRequestStatus.WORKOUT_REQUEST_STATUS_APPROVED
        ]
      },
      getOptions: {
        select: [
          "date",
          "facility.name",
          "horse.trainer.firstName",
          "horse.trainer.middleName",
          "horse.trainer.lastName",
          "createdOn"
        ],
        orderBy: [`date ${SortOrder.ASC}`]
      }
    },
    {
      enabled: Boolean(currentExam?.horse.id)
    }
  );
  const nextWorkoutRequest = upcomingWorkoutRequests?.workoutRequests[0];

  //WarningMessage requests
  const {
    data: horseWarningMessageData,
    isFetching: isPullRelevantHorseWarningMessageFetching,
    refetch: refetchHorseWarningMessage
  } = usePullRelevantHorseWarningMessage(
    {
      horseId: currentExam?.horse?.id
    },
    {
      enabled: Boolean(currentExam?.horse?.id)
    }
  );
  const warningMessage: racehorse360.IRelevantHorseWarningMessage =
    horseWarningMessageData?.relevantHorseWarningMessage;

  const { mutateAsync: saveExam } = useEditHorseExam();
  const { mutateAsync: cancelExam } = useCancelHorseExam({});
  const { mutateAsync: completeExam, isPending: isExamCompleting } =
    useCompleteHorseExam({});

  const closeSubmitModal = () => {
    setIsSubmitModalOpen(false);
  };

  const openCloseOrPauseModal = () => {
    const modalType = isCurrentExamCompleted
      ? EModalConfirmationTypes.EXAM_EDIT_IN_PROGRESS
      : EModalConfirmationTypes.PAUSE_OR_CLOSE;

    dispatchToRedux(setModalState(true));
    dispatchToRedux(setModalType(modalType));
  };

  const openResetModal = () => {
    dispatchToRedux(setModalState(true));
    dispatchToRedux(setModalType(EModalConfirmationTypes.RESET));
  };

  const lockNavigation = () => {
    setBlockClick(true);
    setBlockClickCallback(() => openCloseOrPauseModal);
  };

  const unLockNavigation = () => {
    setBlockClick(false);
    setBlockClickCallback(null);
  };

  const closeVetExamSavingState = () => {
    saveExam({
      horseExam: getVetExamRequestState(currentExam, values)
    }).then(() => {
      refetchData();
      onClose();
    });
  };

  const handleSubmit = () => {
    validateForm()
      .then(errorsObject => {
        const hasErrors = Boolean(Object.keys(errorsObject).length);

        if (hasErrors) {
          scrollToFirstError(ERROR_ANCHOR);
        } else {
          setIsSubmitModalOpen(true);
        }

        setShouldValidateOnChange(hasErrors);
      })
      .catch(error => {
        console.error(error);
      });
  };

  const handleDiscardExam = () => {
    cancelExam({ id: currentExam.id }).then(() => {
      refetchData();
      onClose();
    });
  };

  const handleCompleteExam = (date: Date) => {
    const completedDate = transformViewDate(
      date.toISOString(),
      null,
      "yyyy-MM-dd HH:mm:ss"
    );

    completeExam({
      horseExam: getVetExamRequestState(currentExam, values),
      completedDate,
      timezone: currentTimeZoneString()
    })
      .then(() => {
        refetchData();
        onClose();
      })
      .catch((error: Error) => {
        showErrorSnack(error.message);
      });
  };

  const handleResetExam = () => {
    setValues(getResetLocalStateExam(currentExam));
    dispatchToRedux(setIsUseLastExamEntry(false));
  };

  const handleCloseModal = (shouldCloseExamForm: boolean = false) => {
    dispatchToRedux(setModalState(false));
    dispatchToRedux(setModalType(null));
    shouldCloseExamForm && onClose();
  };

  const handleChangeExamReason = (value: string) => {
    setFieldValue(EVetExamFields.REASON, value);
  };

  const handleChangeRightPanelState = () => {
    setIsShowHorseInfoDesktop(!isShowHorseInfoDesktop);
  };

  const handleCloseHorseInfoMobile = () => {
    setIsShowHorseInfoMobile(false);
  };

  const handleOpenHorseInfoMobile = () => {
    setIsShowHorseInfoMobile(true);
  };

  const handlePauseAndCloseClick = () => {
    closeVetExamSavingState();
  };

  const handleCancelAndCloseClick = () => {
    dispatchToRedux(setModalState(true));
    dispatchToRedux(
      setModalType(EModalConfirmationTypes.EXAM_EDIT_IN_PROGRESS)
    );
  };

  const renderActionsButtons = () => {
    const closeButtonTitle = isCurrentExamCompleted ? (
      <>Cancel &#38; Close</>
    ) : (
      <>Pause &#38; Close</>
    );
    const closeButtonCallback = isCurrentExamCompleted
      ? handleCancelAndCloseClick
      : handlePauseAndCloseClick;
    const submitButtonText = isCurrentExamCompleted
      ? "Save changes"
      : "Submit Exam";

    return (
      <div className={classes.actionButtons}>
        <Button
          color="primary"
          className={clsx(classes.button, classes.pauseAndClose)}
          variant="contained"
          onClick={closeButtonCallback}
          data-test-id={"pauseAndCloseButton"}
        >
          {closeButtonTitle}
        </Button>
        <Button
          color="primary"
          className={clsx(classes.button, classes.submit)}
          variant="contained"
          onClick={handleSubmit}
          data-test-id={"submitExamButton"}
        >
          {submitButtonText}
        </Button>
      </div>
    );
  };

  useEffect(() => {
    if (currentExam) {
      lockNavigation();
      lockExam();
    }

    return () => {
      unLockNavigation();
      unlockExam();
      dispatchToRedux(setIsUseLastExamEntry(false));
    };
  }, [currentExam]);

  const isLoading = [
    isUpcomingWorkoutRequestsLoading,
    isPullRelevantHorseWarningMessageFetching,
    isPastExamsLoading,
    !values,
    isExamCompleting
  ].some(Boolean);

  const isActiveReset = checkSelectingExamFormFields(values);

  return (
    <OverlayPage
      className={classes.overlayPage}
      onClose={openCloseOrPauseModal}
    >
      {isLoading ? (
        <Loader className={classes.pageLoader} />
      ) : (
        <>
          <DialogTitle className={classes.mobileHeader}>
            <Button
              className={classes.closeButton}
              onClick={openCloseOrPauseModal}
            >
              Close
            </Button>
            <span className={classes.mobileTitle}>Vet Exam</span>
            <IconButton disabled={!isActiveReset} onClick={openResetModal}>
              <Refresh />
            </IconButton>
          </DialogTitle>

          <DialogContent className={classes.root}>
            <IconButton
              className={clsx(classes.hideMenuButton, {
                ["revert"]: isShowHorseInfoDesktop
              })}
              onClick={handleChangeRightPanelState}
            >
              <HideMenu />
            </IconButton>

            <div
              className={clsx(classes.vetExamForm, {
                ["showRightPanel"]: isShowHorseInfoDesktop
              })}
            >
              <ExamFormHeader
                horse={currentExam?.horse}
                isActiveReset={isActiveReset}
                isShowHorseInfoDesktop={isShowHorseInfoDesktop}
                onOpenHorseInfoMobile={handleOpenHorseInfoMobile}
                onOpenResetModal={openResetModal}
                warningMessage={warningMessage}
                isPullRelevantHorseWarningMessageFetching={
                  isPullRelevantHorseWarningMessageFetching
                }
                refetchHorseWarningMessage={refetchHorseWarningMessage}
                reasonNote={reasonNote}
                onHeightChange={setExamFormHeaderHeight}
              />

              <div className={classes.scrollableContainer}>
                <CloneLastExamEntry completedExams={completedExams} />

                <FormBlock title="Reason for Exam">
                  <CustomSelect
                    className={classes.reasonsSelect}
                    options={mapOptions(formFieldsItems.reasons)}
                    label="Reason"
                    inputLabel={"Select Reason"}
                    selectedValue={reasonId}
                    onChangeSelectedValue={handleChangeExamReason}
                    dataTestLabel={"examReasonDropdown"}
                    shouldSort={true}
                    disabled={
                      isCurrentExamCompleted && isEventBasedExam(currentExam)
                    }
                  />
                </FormBlock>

                <JogInformation />

                <ExamDetailsList />

                <ExamResults
                  shouldShowConclusionField={shouldShowConclusionField}
                />

                {renderActionsButtons()}
              </div>
            </div>

            <HorseInfoContainer
              horse={currentExam?.horse}
              workoutRequest={nextWorkoutRequest}
              isShowHorseInfoDesktop={isShowHorseInfoDesktop}
              isShowHorseInfoMobile={isShowHorseInfoMobile}
              onCloseHorseInfoMobile={handleCloseHorseInfoMobile}
              completedExams={completedExams}
            />

            {modalState && (
              <VetExamModal
                onCloseModal={handleCloseModal}
                closeVetExamSavingState={closeVetExamSavingState}
                resetExam={handleResetExam}
                discardExam={handleDiscardExam}
                type={modalType}
                horseName={currentExam?.horse?.name}
              />
            )}

            {isSubmitModalOpen && (
              <SubmitExamModal
                onCloseModal={closeSubmitModal}
                completeExam={handleCompleteExam}
                isExamCompleting={isExamCompleting}
                horseName={currentExam?.horse?.name}
                examResult={getExamResult(Number(result))}
                assignedVetId={
                  currentExam?.assignedUserId || currentUser.rh360Id
                }
                withDateField={shouldShowDateCompletionField}
              />
            )}
          </DialogContent>
        </>
      )}
    </OverlayPage>
  );
};

export default React.memo(WithScrollToError<IProps>(VetExams));
